import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useAtomValue } from 'jotai';

import { useContent } from '@/hooks/useContent';
import { useConfig, isYes } from '@/hooks/useConfig';
import { authUserMetaAtom } from '@/store/authAtoms';
import { remoteAccessContextAtom } from '@/store/remoteAccessContextAtom';
import { examService, type DicomMediaListItem, type ExamRecord } from '@/services/examService';
import { messageService, messageKeys } from '@/services/messageService';
import { hasOpenableReport } from '@/lib/examRecords';
import { useLiteMode } from '@/hooks/useLiteMode';
import { useSessionCaps } from '@/hooks/useSessionCaps';

import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const SEEN_MEDIA_KEY = 'pp_seen_dicom_media';
const DISMISS_EXAM_SOON_KEY = 'pp_notif_exam_soon_dismissed';

type NotifKind = 'examSoon' | 'downloadReady' | 'newResults' | 'readyToSchedule' | 'message';

interface PortalNotification {
  id: string;
  kind: NotifKind;
  title: string;
  whenLabel: string;
  unread: boolean;
  urgent?: boolean;
  href: string;
  state?: Record<string, unknown>;
}

function getSeenMediaSet(): Set<number> {
  try { return new Set<number>(JSON.parse(localStorage.getItem(SEEN_MEDIA_KEY) ?? '[]') as number[]); }
  catch { return new Set(); }
}

function relativeWhen(iso?: string | null): string {
  if (!iso) return '';
  const d = dayjs(iso);
  if (!d.isValid()) return '';
  const mins = dayjs().diff(d, 'minute');
  if (mins < 60) return `${Math.max(mins, 1)} mins ago`;
  const hours = dayjs().diff(d, 'hour');
  if (hours < 24) return `${hours} hr ago`;
  return d.format('dddd');
}

function kindIcon(kind: NotifKind): ReactNode {
  const sx = { fontSize: 24, color: 'primary.main' } as const;
  switch (kind) {
    case 'examSoon':
    case 'readyToSchedule':
      return <CalendarTodayOutlinedIcon sx={sx} />;
    case 'newResults':
      return <AssignmentOutlinedIcon sx={sx} />;
    case 'downloadReady':
      return <DownloadOutlinedIcon sx={sx} />;
    case 'message':
      return <ChatOutlinedIcon sx={sx} />;
  }
}

function parseExamSoonWindow(raw: string | undefined): number {
  const n = Number.parseInt(raw ?? '', 10);
  return Number.isFinite(n) && n >= 0 ? n : 3;
}

/**
 * Header bell + All/Unread panel (popover on desktop, drawer on mobile).
 * Surfaces exam-soon, download-ready, new results, ready-to-schedule, and unread messages
 * from existing dashboard/query caches — not a separate notifications product.
 */
export function PortalNotifications() {
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const navigate = useNavigate();
  const { getContentString } = useContent();
  const lite = useLiteMode();
  const { isGuestSession, isICodeSession } = useSessionCaps();
  const user = useAtomValue(authUserMetaAtom);
  const isRemoteAccess = useAtomValue(remoteAccessContextAtom) !== null;
  const messagingEnabled = isYes(useConfig('PPMessagesAccessEnabled'));
  const pacsImageDownloading = isYes(useConfig('PACSImageDownloading'));
  const examSoonWindow = parseExamSoonWindow(useConfig('DaysBeforeExamToShowNotification'));

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const open = isDesktop ? Boolean(anchorEl) : mobileOpen;

  const canFetchWorklist = !!user?.id && !isGuestSession && !isICodeSession;
  const canFetchMessages = canFetchWorklist && !isRemoteAccess && messagingEnabled && lite.isSectionVisible('messages');

  const { data: examRecords = [] } = useQuery({
    queryKey: ['exams', user?.id, 'dashboard'],
    queryFn: () => examService.search({ Skip: 0, Take: 200, ListType: 'exams' }).then((d) => d.result),
    enabled: canFetchWorklist && lite.isSectionVisible('exams'),
    staleTime: 2 * 60 * 1000,
    retry: 1,
  });

  const { data: apptRecords = [] } = useQuery({
    queryKey: ['appointments', user?.id, 'dashboard'],
    queryFn: () => examService.search({ Skip: 0, Take: 200, ListType: 'appointments' }).then((d) => d.result),
    enabled: canFetchWorklist && lite.isSectionVisible('appointments'),
    staleTime: 2 * 60 * 1000,
    retry: 1,
  });

  const { data: inboxPage } = useQuery({
    queryKey: messageKeys.inbox(1, user?.id),
    queryFn: () => messageService.getInbox(1),
    enabled: canFetchMessages,
    staleTime: 2 * 60 * 1000,
    retry: false,
  });

  const { data: activeDicomMedia = [] } = useQuery({
    queryKey: ['dicomMediaActive', user?.id],
    queryFn: () => examService.listActiveDicomMedia(),
    enabled: canFetchWorklist && !isRemoteAccess && pacsImageDownloading,
    staleTime: 60_000,
    retry: false,
  });

  const notifications = useMemo(() => {
    const items: PortalNotification[] = [];
    const now = dayjs();
    const dismissedExamSoon = sessionStorage.getItem(DISMISS_EXAM_SOON_KEY) === '1';
    const seenMedia = getSeenMediaSet();

    const upcoming = (apptRecords as ExamRecord[])
      .filter((r) => r.reportStatus === 'Scheduled' && dayjs(r.studyDate).isAfter(now))
      .sort((a, b) => dayjs(a.studyDate).diff(dayjs(b.studyDate)));
    const next = upcoming[0];
    if (next && !dismissedExamSoon) {
      const days = dayjs(next.studyDate).startOf('day').diff(now.startOf('day'), 'day');
      if (days >= 0 && days <= examSoonWindow) {
        items.push({
          id: `exam-soon-${next.studyKey}`,
          kind: 'examSoon',
          title: getContentString('PPNotificationsExamSoon') || 'Upcoming appointment',
          whenLabel: days === 0
            ? (getContentString('PPDashboardNextApptToday') || 'Today')
            : days === 1
              ? (getContentString('PPDashboardNextApptTomorrow') || 'Tomorrow')
              : (getContentString('PPDashboardNextApptInDays') || 'In {{count}} days').replace('{{count}}', String(days)),
          unread: true,
          href: '/appointments',
          state: { selectedApptId: String(next.studyKey) },
        });
      }
    }

    const readyDownloads = (activeDicomMedia as DicomMediaListItem[]).filter(
      (i) => (i.isComplete || i.isAvailable) && !seenMedia.has(i.mediaRequestKey),
    );
    for (const media of readyDownloads.slice(0, 3)) {
      items.push({
        id: `download-${media.mediaRequestKey}`,
        kind: 'downloadReady',
        title: getContentString('PPNotificationsDownloadReady') || 'Images ready to download',
        whenLabel: media.examDescription || media.accessionNumber || '',
        unread: true,
        href: media.studyKey > 0 ? '/exams' : '/account',
        state: media.studyKey > 0
          ? { selectedExamId: String(media.studyKey) }
          : { accountSection: 'imageDownload' },
      });
    }

    const newResults = (examRecords as ExamRecord[]).filter((r) => hasOpenableReport(r) && r.isNewReport === true);
    if (newResults.length > 0) {
      const newest = [...newResults].sort((a, b) => dayjs(b.studyDate).valueOf() - dayjs(a.studyDate).valueOf())[0];
      items.push({
        id: `new-results-${newest.studyKey}`,
        kind: 'newResults',
        title: getContentString('PPNotificationsNewResults') || 'Results',
        whenLabel: relativeWhen(newest.studyDate),
        unread: true,
        urgent: true,
        href: '/exams',
        state: { selectedExamId: String(newest.studyKey) },
      });
    }

    const pending = (apptRecords as ExamRecord[]).filter((r) => r.reportStatus !== 'Scheduled');
    if (pending.length > 0) {
      items.push({
        id: `ots-${pending[0].studyKey}`,
        kind: 'readyToSchedule',
        title: getContentString('PPNotificationsReadyToSchedule') || 'Appointment ready to schedule',
        whenLabel: relativeWhen(pending[0].studyDate) || (pending[0].procedureDescription || ''),
        unread: true,
        href: '/appointments',
      });
    }

    if (inboxPage?.messagingEnabled !== false) {
      for (const msg of (inboxPage?.items ?? []).slice(0, 5)) {
        items.push({
          id: `msg-${msg.messageId}`,
          kind: 'message',
          title: getContentString('PPNotificationsNewMessage') || 'Message: Provider has sent you a message',
          whenLabel: relativeWhen(msg.sentDateTime),
          unread: !msg.readFlag,
          href: '/messages',
        });
      }
    }

    return items;
  }, [apptRecords, examRecords, activeDicomMedia, inboxPage, examSoonWindow, getContentString]);

  const unreadCount = notifications.filter((n) => n.unread).length;
  const visible = filter === 'unread' ? notifications.filter((n) => n.unread) : notifications;

  const handleOpen = (el: HTMLElement) => {
    if (isDesktop) setAnchorEl(el);
    else setMobileOpen(true);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setMobileOpen(false);
  };

  const handleSelect = (item: PortalNotification) => {
    if (item.kind === 'examSoon') sessionStorage.setItem(DISMISS_EXAM_SOON_KEY, '1');
    handleClose();
    navigate(item.href, item.state ? { state: item.state } : undefined);
  };

  const filterTabs = (
    <Box
      sx={{
        bgcolor: '#f5f5f5',
        borderRadius: 3,
        p: '6px',
        display: 'flex',
        gap: '6px',
        width: '100%',
      }}
      role="tablist"
      aria-label={getContentString('PPNotificationsTitle') || 'Notifications'}
    >
      {([
        { value: 'all' as const, label: getContentString('PPNotificationsAll') || 'All' },
        { value: 'unread' as const, label: getContentString('PPNotificationsUnread') || 'Unread' },
      ]).map((tab) => {
        const selected = filter === tab.value;
        return (
          <Button
            key={tab.value}
            role="tab"
            aria-selected={selected}
            onClick={() => setFilter(tab.value)}
            sx={{
              flex: 1,
              minHeight: 32,
              py: 0.5,
              px: 2,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.8125rem',
              lineHeight: '22px',
              bgcolor: selected ? 'background.paper' : 'transparent',
              color: selected ? 'primary.main' : 'text.secondary',
              boxShadow: 'none',
              '&:hover': { bgcolor: selected ? 'background.paper' : 'action.hover' },
            }}
          >
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', justifyContent: 'center' }}>
              <span>{tab.label}</span>
              {tab.value === 'unread' && unreadCount > 0 && (
                <Box
                  component="span"
                  sx={{
                    minWidth: 20,
                    height: 20,
                    px: 0.75,
                    borderRadius: '100px',
                    bgcolor: 'info.main',
                    color: 'info.contrastText',
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: '20px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {unreadCount}
                </Box>
              )}
            </Stack>
          </Button>
        );
      })}
    </Box>
  );

  const list = (
    <Stack spacing={1.25} sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
      {visible.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 2 }}>
          {getContentString('PPNotificationsEmpty') || 'No notifications'}
        </Typography>
      )}
      {visible.map((item) => (
        <Button
          key={item.id}
          onClick={() => handleSelect(item)}
          sx={{
            justifyContent: 'flex-start',
            textAlign: 'start',
            textTransform: 'none',
            color: 'text.primary',
            bgcolor: '#f5f5f5',
            borderRadius: 3,
            minHeight: 72,
            px: 2,
            py: 1.5,
            gap: 1,
            '&:hover': { bgcolor: 'action.selected' },
            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
          }}
        >
          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'flex-start', pt: 0.25 }}>{kindIcon(item.kind)}</Box>
          <Box sx={{ minWidth: 0, flex: 1, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontWeight: 500, fontSize: '0.875rem', lineHeight: '18px' }}>{item.title}</Typography>
              {item.whenLabel && (
                <Typography sx={{ fontWeight: 400, fontSize: '0.875rem', lineHeight: '20px', letterSpacing: '0.25px', color: 'text.primary', mt: 0.25 }}>
                  {item.whenLabel}
                </Typography>
              )}
            </Box>
            {item.urgent && (
              <Chip
                size="small"
                label={getContentString('PPMessagesUrgent') || 'Urgent'}
                sx={{
                  height: 24,
                  fontSize: '0.8125rem',
                  fontWeight: 400,
                  bgcolor: 'action.selected',
                  color: 'text.primary',
                  flexShrink: 0,
                }}
              />
            )}
          </Box>
          {item.unread
            ? <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'info.main', flexShrink: 0 }} aria-hidden />
            : <Box sx={{ width: 8, flexShrink: 0 }} aria-hidden />}
        </Button>
      ))}
    </Stack>
  );

  const desktopPanel = (
    <Box sx={{ width: 528, maxHeight: 520, display: 'flex', flexDirection: 'column', gap: 1.25, p: 2 }}>
      <Typography sx={{ fontWeight: 400, fontSize: '1.125rem', lineHeight: '24px', color: 'text.primary' }}>
        {getContentString('PPNotificationsTitle') || 'Notifications'}
      </Typography>
      {filterTabs}
      {list}
    </Box>
  );

  const mobilePanel = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 1.25, px: 1.5, pt: 5, pb: 2 }}>
      <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
        <IconButton
          onClick={handleClose}
          aria-label={getContentString('PortalBack') || getContentString('PortalClose') || 'Back'}
          sx={{ minWidth: 40, minHeight: 40 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography sx={{ fontWeight: 400, fontSize: '1.125rem', lineHeight: '24px', color: 'text.primary' }}>
          {getContentString('PPNotificationsTitle') || 'Notifications'}
        </Typography>
      </Stack>
      {filterTabs}
      {list}
    </Box>
  );

  return (
    <>
      <IconButton
        color="inherit"
        aria-label={`${getContentString('PPNotificationsTitle') || 'Notifications'}${unreadCount > 0 ? ` (${unreadCount})` : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={(e) => handleOpen(e.currentTarget)}
        sx={{ minWidth: 44, minHeight: 44, color: 'text.secondary' }}
      >
        <Badge
          badgeContent={unreadCount}
          color="info"
          max={99}
          sx={{ '& .MuiBadge-badge': { fontSize: 10, minWidth: 16, height: 16 } }}
        >
          <NotificationsNoneOutlinedIcon />
        </Badge>
      </IconButton>

      {isDesktop ? (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0px 8px 24px -3px rgba(16,24,40,0.1), 0px 8px 24px -3px rgba(16,24,40,0.05)',
              },
            },
          }}
        >
          {desktopPanel}
        </Popover>
      ) : (
        <Dialog
          fullScreen
          open={open}
          onClose={handleClose}
          PaperProps={{ sx: { bgcolor: 'background.default' } }}
        >
          {mobilePanel}
        </Dialog>
      )}
    </>
  );
}

/** Shared badge counts for bottom nav / sidebar (reuses Dashboard worklist query keys). */
export function usePortalNavBadges() {
  const user = useAtomValue(authUserMetaAtom);
  const isRemoteAccess = useAtomValue(remoteAccessContextAtom) !== null;
  const { isGuestSession, isICodeSession } = useSessionCaps();
  const lite = useLiteMode();
  const messagingEnabled = isYes(useConfig('PPMessagesAccessEnabled'));
  const canFetchWorklist = !!user?.id && !isGuestSession && !isICodeSession;

  const { data: examRecords = [] } = useQuery({
    queryKey: ['exams', user?.id, 'dashboard'],
    queryFn: () => examService.search({ Skip: 0, Take: 200, ListType: 'exams' }).then((d) => d.result),
    enabled: canFetchWorklist && lite.isSectionVisible('exams'),
    staleTime: 2 * 60 * 1000,
    retry: 1,
  });

  const { data: apptRecords = [] } = useQuery({
    queryKey: ['appointments', user?.id, 'dashboard'],
    queryFn: () => examService.search({ Skip: 0, Take: 200, ListType: 'appointments' }).then((d) => d.result),
    enabled: canFetchWorklist && lite.isSectionVisible('appointments'),
    staleTime: 2 * 60 * 1000,
    retry: 1,
  });

  const { data: inboxPage } = useQuery({
    queryKey: messageKeys.inbox(1, user?.id),
    queryFn: () => messageService.getInbox(1),
    enabled: canFetchWorklist && !isRemoteAccess && messagingEnabled && lite.isSectionVisible('messages'),
    staleTime: 2 * 60 * 1000,
    retry: false,
  });

  const newResults = useMemo(
    () => examRecords.filter((r) => hasOpenableReport(r) && r.isNewReport === true).length,
    [examRecords],
  );
  const upcoming = useMemo(() => {
    const now = dayjs();
    return apptRecords.filter((r) => r.reportStatus === 'Scheduled' && dayjs(r.studyDate).isAfter(now)).length;
  }, [apptRecords]);
  const unreadMessages =
    inboxPage?.messagingEnabled === false
      ? 0
      : (inboxPage?.items ?? []).filter((item) => !item.readFlag).length;

  return { newResults, upcoming, unreadMessages };
}
